import * as React from 'react';
import { isSupabase } from '@/lib/supabase';
import {
  getLessonProgress,
  saveLessonProgressAndSync,
  type LessonProgressRow,
} from '@/repos/progressRepo';

const SAVE_DEBOUNCE_MS = 12000;
const COMPLETION_THRESHOLD = 90;

export interface UseLessonProgressOptions {
  authUserId?: string;
  systemUserId?: string;
  lessonId?: string;
  moduleId?: string;
  courseId?: string;
  tenantId?: string;
  enabled?: boolean;
}

export function useLessonProgress({
  authUserId,
  systemUserId,
  lessonId,
  moduleId,
  courseId,
  tenantId,
  enabled = true,
}: UseLessonProgressOptions) {
  const [savedProgress, setSavedProgress] = React.useState<LessonProgressRow | null>(null);
  const [savedPercentage, setSavedPercentage] = React.useState(0);
  const [lastPosition, setLastPosition] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [isCompleted, setIsCompleted] = React.useState(false);

  const lastSaveRef = React.useRef(0);
  const pendingRef = React.useRef<{ current: number; total: number; updateRef: string } | null>(null);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTickRef = React.useRef<number | null>(null);
  const warnedCanPersistRef = React.useRef(false);

  const canPersist =
    enabled &&
    isSupabase() &&
    !!authUserId &&
    !!lessonId &&
    !!moduleId &&
    !!courseId &&
    !!tenantId;

  React.useEffect(() => {
    if (canPersist || warnedCanPersistRef.current) return;
    warnedCanPersistRef.current = true;
    console.warn('⚠️ useLessonProgress: persistência desabilitada', {
      enabled,
      isSupabase: isSupabase(),
      authUserId: !!authUserId,
      lessonId: !!lessonId,
      moduleId: !!moduleId,
      courseId: !!courseId,
      tenantId: !!tenantId,
    });
  }, [canPersist, enabled, authUserId, lessonId, moduleId, courseId, tenantId]);

  React.useEffect(() => {
    warnedCanPersistRef.current = false;
  }, [lessonId]);

  const loadProgress = React.useCallback(async () => {
    if (!authUserId || !lessonId || !isSupabase()) {
      setSavedProgress(null);
      setSavedPercentage(0);
      setLastPosition(0);
      setIsCompleted(false);
      return;
    }

    try {
      setLoading(true);
      const row = await getLessonProgress(authUserId, lessonId);
      setSavedProgress(row);
      setSavedPercentage(row?.progress_percentage ?? 0);
      setLastPosition(row?.last_position ?? 0);
      setIsCompleted(!!row?.is_completed);
    } catch (e) {
      console.error('❌ useLessonProgress: erro ao carregar:', e);
    } finally {
      setLoading(false);
    }
  }, [authUserId, lessonId]);

  React.useEffect(() => {
    loadProgress();
    lastSaveRef.current = 0;
    pendingRef.current = null;
    lastTickRef.current = null;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, [loadProgress, lessonId]);

  const flushSave = React.useCallback(
    async (explicitComplete = false) => {
      if (!canPersist || !pendingRef.current) return;

      const { current, total, updateRef } = pendingRef.current;
      const pct = total > 0 ? Math.round((current / total) * 100) : 0;
      const now = Date.now();
      const timeSpentDelta =
        lastTickRef.current != null
          ? Math.max(0, Math.round((now - lastTickRef.current) / 1000))
          : 0;
      lastTickRef.current = now;

      try {
        const row = await saveLessonProgressAndSync({
          authUserId: authUserId!,
          systemUserId,
          lessonId: lessonId!,
          moduleId: moduleId!,
          courseId: courseId!,
          tenantId: tenantId!,
          progressPercentage: explicitComplete ? 100 : pct,
          lastPosition: Math.round(current),
          timeSpentDelta,
          forceComplete: explicitComplete,
          progressUpdateRef: updateRef,
        });
        setSavedProgress(row);
        setSavedPercentage(row.progress_percentage);
        setLastPosition(row.last_position);
        setIsCompleted(!!row.is_completed);
        lastSaveRef.current = Date.now();
        pendingRef.current = null;
      } catch (e) {
        console.error('❌ useLessonProgress: erro ao salvar lição:', e);
      }
    },
    [canPersist, authUserId, systemUserId, lessonId, moduleId, courseId, tenantId]
  );

  const saveProgress = React.useCallback(
    (currentTime: number, duration: number) => {
      if (!canPersist || duration <= 0) return;

      pendingRef.current = pendingRef.current
        ? { ...pendingRef.current, current: currentTime, total: duration }
        : { current: currentTime, total: duration, updateRef: crypto.randomUUID() };
      const pct = Math.round((currentTime / duration) * 100);

      if (pct >= COMPLETION_THRESHOLD && !isCompleted) {
        void flushSave(false);
        return;
      }

      const elapsed = Date.now() - lastSaveRef.current;
      if (elapsed >= SAVE_DEBOUNCE_MS) {
        void flushSave(false);
        return;
      }

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        void flushSave(false);
      }, SAVE_DEBOUNCE_MS - elapsed);
    },
    [canPersist, isCompleted, flushSave]
  );

  const persistProgress = React.useCallback(
    async (
      currentTime: number,
      totalDuration: number,
      options?: { forceComplete?: boolean }
    ) => {
      if (!canPersist || totalDuration <= 0) return;

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      pendingRef.current = pendingRef.current
        ? { ...pendingRef.current, current: currentTime, total: totalDuration }
        : { current: currentTime, total: totalDuration, updateRef: crypto.randomUUID() };
      const pct = Math.round((currentTime / totalDuration) * 100);
      await flushSave(options?.forceComplete === true);
    },
    [canPersist, isCompleted, flushSave]
  );

  const markComplete = React.useCallback(async () => {
    if (!canPersist) return;
    pendingRef.current = pendingRef.current ?? { current: 0, total: 1, updateRef: crypto.randomUUID() };
    await flushSave(true);
  }, [canPersist, flushSave]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (canPersist && pendingRef.current) {
        void flushSave(false);
      }
    };
  }, [canPersist, flushSave, lessonId]);

  return {
    savedProgress,
    savedPercentage,
    lastPosition,
    isCompleted,
    loading,
    saveProgress,
    persistProgress,
    markComplete,
    reload: loadProgress,
  };
}
